import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { api } from '@/utils/api'

export interface User {
  id: number
  telegram_id?: number | null
  username?: string | null
  name: string
  work_start: string
  daily_hours: number
  role: 'user' | 'admin'
  is_active: boolean
  created_at: string
}

interface AuthContextType {
  user: User | null
  token: string | null
  login: (username: string, password: string) => Promise<void>
  registerWithInvite: (inviteCode: string, name: string, username: string, password: string) => Promise<void>
  loginWithTelegram: (telegramId?: number) => Promise<void>
  logout: () => void
  updateUser: (data: Partial<User>) => Promise<void>
  isLoading: boolean
  isAuthenticated: boolean
  isAdmin: boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

const getTgUserId = (): number | undefined => {
  try {
    return (window as any).Telegram?.WebApp?.initDataUnsafe?.user?.id
  } catch {
    return undefined
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [token, setToken] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  const verifyTokenAndFetchMe = async (accessToken: string): Promise<boolean> => {
    try {
      const meRes = await api.get('/auth/me', {
        headers: { Authorization: `Bearer ${accessToken}` }
      })
      setUser(meRes.data)
      setToken(accessToken)
      localStorage.setItem('attendance_token', accessToken)
      localStorage.setItem('attendance_user', JSON.stringify(meRes.data))
      return true
    } catch (e) {
      localStorage.removeItem('attendance_token')
      localStorage.removeItem('attendance_user')
      setToken(null)
      setUser(null)
      return false
    }
  }

  // Initialize auth state
  useEffect(() => {
    const initAuth = async () => {
      setIsLoading(true)
      try {
        // 1. If inside Telegram WebApp, try auto-login if account is already linked
        const tgId = getTgUserId()
        if (tgId) {
          try {
            const authRes = await api.post('/auth/telegram', { telegram_id: tgId })
            const accessToken = authRes.data.access_token
            if (accessToken) {
              const ok = await verifyTokenAndFetchMe(accessToken)
              if (ok) return
            }
          } catch (err) {
            // Not yet linked or not yet registered, fall through to token check or login screen
          }
        }

        // 2. Check saved token in localStorage
        const savedToken = localStorage.getItem('attendance_token')
        if (savedToken) {
          const ok = await verifyTokenAndFetchMe(savedToken)
          if (ok) {
            // If logged in via stored token inside Telegram, link telegram_id
            if (tgId) {
              try {
                await api.post('/auth/link-telegram', { telegram_id: tgId }, {
                  headers: { Authorization: `Bearer ${savedToken}` }
                })
              } catch {}
            }
            return
          }
        }
      } finally {
        setIsLoading(false)
      }
    }

    initAuth()
  }, [])

  // Standard Username / Password login (with automatic Telegram ID linking)
  const login = useCallback(async (username: string, password: string) => {
    setIsLoading(true)
    try {
      const tgId = getTgUserId()
      const res = await api.post('/auth/login', {
        username,
        password,
        telegram_id: tgId || undefined
      })
      const accessToken = res.data.access_token
      await verifyTokenAndFetchMe(accessToken)
    } finally {
      setIsLoading(false)
    }
  }, [])

  // Register with Invitation Code (with automatic Telegram ID linking)
  const registerWithInvite = useCallback(async (invite_code: string, name: string, username: string, password: string) => {
    setIsLoading(true)
    try {
      const tgId = getTgUserId()
      const res = await api.post('/auth/register-invite', {
        invite_code,
        name,
        username,
        password,
        telegram_id: tgId || undefined
      })
      const accessToken = res.data.access_token
      await verifyTokenAndFetchMe(accessToken)
    } finally {
      setIsLoading(false)
    }
  }, [])

  // Telegram direct auth
  const loginWithTelegram = useCallback(async (telegramId?: number) => {
    setIsLoading(true)
    try {
      const tgId = telegramId || getTgUserId()
      if (!tgId) {
        throw new Error('شناسه تلگرام یافت نشد.')
      }
      const res = await api.post('/auth/telegram', { telegram_id: tgId })
      await verifyTokenAndFetchMe(res.data.access_token)
    } finally {
      setIsLoading(false)
    }
  }, [])

  const logout = useCallback(() => {
    setToken(null)
    setUser(null)
    localStorage.removeItem('attendance_token')
    localStorage.removeItem('attendance_user')
  }, [])

  const updateUser = useCallback(async (data: Partial<User>) => {
    try {
      const res = await api.patch('/auth/me', data)
      setUser(res.data)
      localStorage.setItem('attendance_user', JSON.stringify(res.data))
    } catch (e) {
      if (user) {
        const updated = { ...user, ...data } as User
        setUser(updated)
        localStorage.setItem('attendance_user', JSON.stringify(updated))
      }
    }
  }, [user])

  return (
    <AuthContext.Provider value={{
      user,
      token,
      login,
      registerWithInvite,
      loginWithTelegram,
      logout,
      updateUser,
      isLoading,
      isAuthenticated: !!user,
      isAdmin: user?.role === 'admin'
    }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
